import { useCallback, useEffect, useRef, useState } from 'react';
import { BlockStack, Button, Text, TextField } from '@shopify/polaris';
import { Select } from '../forms';
import { returnRequest, type ReturnAccount } from '../../../../packages/shared/supplier-returns/client';
import type { ReturnChoice } from './Choice';
import { returnBase, useReturnCurrent } from './account';

type Props = {
  account: ReturnAccount; target: number | null; kind: 'supplier' | 'warehouse'; id: string;
  value: string; selectedLabel?: string; error?: string; disabled: boolean;
  onSelect: (choice: ReturnChoice | null) => void;
};
type ChoicePage = { target_id: number | null; choices: ReturnChoice[]; next: number | string | null };

export function SupplierWarehouseSelect({ account, target, kind, id, value, selectedLabel, error, disabled, onSelect }: Props) {
  const isSupplier = kind === 'supplier';
  const label = isSupplier ? 'Supplier' : 'Warehouse';
  const plural = isSupplier ? 'suppliers' : 'warehouses';
  const current = useReturnCurrent();
  const generation = useRef(0);
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<ReturnChoice[]>([]);
  const [next, setNext] = useState<ChoicePage['next']>(null);
  const [filterable, setFilterable] = useState(false);
  const [busy, setBusy] = useState(true);
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async (after: ChoicePage['next'] = null) => {
    const run = ++generation.current;
    const active = () => current() && run === generation.current;
    const params = new URLSearchParams({ kind, q: query });
    if (after !== null) params.set('after', String(after));
    setBusy(true);
    setLoadError('');
    try {
      const page = await returnRequest<ChoicePage>(returnBase, account, `${target ?? 'new'}/choices?${params}`, active);
      if (!active()) return;
      if (page.target_id !== target) throw new Error('These choices belong to a different return.');
      setRows(previous => after === null ? page.choices : [...previous, ...page.choices]);
      setNext(page.next);
      if (query === '' && after === null) setFilterable(page.next !== null);
    } catch (cause) {
      if (active()) setLoadError(cause instanceof Error ? cause.message : `Could not load ${plural}.`);
    } finally {
      if (active()) setBusy(false);
    }
  }, [account, current, kind, plural, query, target]);

  useEffect(() => {
    setRows([]);
    setNext(null);
    setBusy(true);
    setLoadError('');
    const timer = window.setTimeout(() => void load(), query ? 180 : 0);
    return () => { window.clearTimeout(timer); generation.current++; };
  }, [load, query]);

  const options = rows.map(row => ({ label: row.label, value: String(row.id) }));
  if (value && !options.some(option => option.value === value)) {
    options.unshift({ label: selectedLabel || `Saved ${kind} #${value}`, value });
  }
  const help = busy && !rows.length ? `Loading ${plural}…`
    : !loadError && !rows.length ? (query ? `No ${plural} match this filter.` : `No ${isSupplier ? 'active suppliers' : 'warehouses'} are available.`)
    : isSupplier ? 'Who will receive the goods?' : 'Where are the goods being returned from?';

  return <BlockStack gap="150">
    <Select id={id} label={label} requiredIndicator options={[{ label: `Choose a ${kind}`, value: '' }, ...options]}
      value={value} disabled={disabled} helpText={help} error={error} onChange={v => {
        if (!v) onSelect(null);
        else { const row = rows.find(choice => String(choice.id) === v); if (row) onSelect(row); }
      }} />
    {filterable && <TextField label={`Filter ${plural}`} value={query} onChange={setQuery}
      autoComplete="off" clearButton onClearButtonClick={() => setQuery('')} disabled={disabled} maxLength={100} />}
    {loadError && <div role="alert"><Text as="p" tone="critical">{loadError}</Text>
      <Button variant="plain" disabled={disabled || busy} onClick={() => void load(next)}>Retry loading {plural}</Button></div>}
    {next !== null && !loadError && <Button variant="plain" disabled={disabled || busy} onClick={() => void load(next)}>More {plural}</Button>}
  </BlockStack>;
}
